import * as B from '@blueprintjs/core';
import {useQuery} from '@tanstack/react-query';
import classnames from 'classnames';
import moment from 'moment';
import React from 'react';
import {Column, useFlexLayout, useTable} from 'react-table';

import {imageryPrepaidsQuery, prepaidRemainingCents} from 'app/modules/Cart';
import type {ApiImageryPrepaid} from 'app/modules/Remote/Organization';
import {formatCost} from 'app/utils/costUtils';

import AddCreditDialog from './AddCreditDialog';
import cs from './ContractHistory.module.scss';

const DATE_FORMAT = 'MM/DD/YYYY';

const NUMERIC_COLUMNS = new Set(['cents', 'usedCents', 'remainingCents']);

const KIND_LABELS: Record<ApiImageryPrepaid['kind'], string> = {
  credit: 'Prepaid credit',
  refund: 'Refunded order',
};

const PrepaidSourcesTable: React.FC<{prepaids: ApiImageryPrepaid[]}> = ({prepaids}) => {
  const columns = React.useMemo<Column<ApiImageryPrepaid>[]>(
    () => [
      {
        Header: 'Added',
        accessor: 'createdAt',
        Cell: ({cell}) => moment(cell.value as string).format(DATE_FORMAT),
        width: 60,
      },
      {
        Header: 'Source',
        accessor: 'kind',
        Cell: ({cell}) => KIND_LABELS[cell.value as ApiImageryPrepaid['kind']],
      },
      {
        Header: 'Amount',
        accessor: 'cents',
        Cell: ({cell}) => formatCost(cell.value as number),
        width: 60,
      },
      {
        Header: 'Used',
        id: 'usedCents',
        accessor: (prepaid) => prepaid.cents - prepaid.remainingCents,
        Cell: ({cell}) => formatCost(cell.value as number),
        width: 60,
      },
      {
        Header: 'Remaining',
        accessor: 'remainingCents',
        Cell: ({cell}) => formatCost(cell.value as number),
        width: 60,
      },
    ],
    []
  );

  const {getTableProps, getTableBodyProps, headerGroups, rows, prepareRow} = useTable(
    {columns, data: prepaids},
    useFlexLayout
  );

  return (
    <div className={cs.tableWrapper}>
      <table {...getTableProps()} className={cs.table}>
        <thead>
          {headerGroups.map((headerGroup, i) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={i}>
              {headerGroup.headers.map((column, i) => (
                <th
                  {...column.getHeaderProps()}
                  className={classnames({[cs.numeric]: NUMERIC_COLUMNS.has(column.id)})}
                  key={i}
                >
                  <div>{column.render('Header')}</div>
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} key={i}>
                {row.cells.map((cell, i) => (
                  <td
                    {...cell.getCellProps()}
                    className={classnames({[cs.numeric]: NUMERIC_COLUMNS.has(cell.column.id)})}
                    key={i}
                  >
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const ImageryPrepaidSection: React.FC<{organizationId: string; canAddPrepaid: boolean}> = ({
  organizationId,
  canAddPrepaid,
}) => {
  const {data: prepaids, status, refetch} = useQuery(imageryPrepaidsQuery(organizationId));
  const [showSources, setShowSources] = React.useState(false);
  const [isAddPrepaidOpen, setIsAddPrepaidOpen] = React.useState(false);

  return (
    <div className={cs.planBillingCard}>
      <div className={cs.imageryPrepaidHeader}>
        <div>
          <div className={cs.sectionLabel}>Prepaid imagery credit</div>
          {status === 'pending' && (
            <div className={classnames(cs.sectionValue, 'bp5-skeleton')}>$0.00 available</div>
          )}
          {status === 'error' && (
            <div className={cs.sectionSub}>
              We weren’t able to load your credit right now.{' '}
              <a onClick={() => refetch()}>Try again</a>
            </div>
          )}
          {status === 'success' && (
            <>
              <div className={cs.sectionValue}>
                {formatCost(prepaidRemainingCents(prepaids))} available
              </div>
              <div className={cs.sectionSub}>
                Used at checkout toward imagery (before tax), oldest credit first.
              </div>
            </>
          )}
        </div>
        <B.Tooltip disabled={canAddPrepaid} content="Only admins can add credit">
          <B.AnchorButton
            icon="plus"
            intent={B.Intent.PRIMARY}
            onClick={() => setIsAddPrepaidOpen(true)}
            disabled={!canAddPrepaid}
          >
            Add credit
          </B.AnchorButton>
        </B.Tooltip>
        <AddCreditDialog
          organizationId={organizationId}
          isOpen={isAddPrepaidOpen}
          onClose={() => setIsAddPrepaidOpen(false)}
        />
      </div>
      {status === 'success' && prepaids.length > 0 && (
        <div className={cs.imageryPrepaidSources}>
          <B.Button
            minimal
            small
            icon={showSources ? 'chevron-down' : 'chevron-right'}
            onClick={() => setShowSources((shown) => !shown)}
          >
            {showSources ? 'Hide sources' : `Show sources (${prepaids.length})`}
          </B.Button>
          <B.Collapse isOpen={showSources}>
            <PrepaidSourcesTable prepaids={prepaids} />
          </B.Collapse>
        </div>
      )}
    </div>
  );
};

export default ImageryPrepaidSection;
